import {
  getMinuteOfDay,
  isRuleEnforcingNow,
  isUserForcedStopSuppression,
  type DeviceScheduleClock,
} from "../../lib/rules/ruleSchedule";
import type { Rule, RuleConfig, Target } from "../../lib/rules/ruleTypes";
import { dedupeAppsFromTargets, dedupeDomainsFromTargets } from "./TargetPreview";

export type BlockedItems = {
  appKeys: Set<string>;
  domains: Set<string>;
};

function scheduleClock(now: Date): DeviceScheduleClock {
  return {
    dayOfWeek: now.getDay(),
    minuteOfDay: getMinuteOfDay(now),
    nowMs: now.getTime(),
  };
}

/** Same "in vigore" notion as swipe unlock, excluding break/forced suspend. */
export function isRuleActivelyEnforcingForSwipeGuard(
  rule: RuleConfig,
  now: Date = new Date(),
): boolean {
  if (!rule.enabled) return false;
  if (!isRuleEnforcingNow(rule, now)) return false;
  if (isUserForcedStopSuppression(rule, scheduleClock(now))) return false;
  return true;
}

function normalizeDomains(domains: string[]): Set<string> {
  return new Set(domains.map((domain) => domain.trim().toLowerCase()).filter(Boolean));
}

export function deriveBlockedItemsForTarget(target: Target): BlockedItems {
  return {
    appKeys: new Set(dedupeAppsFromTargets([target]).map((app) => app.key)),
    domains: normalizeDomains(dedupeDomainsFromTargets([target])),
  };
}

export function deriveBlockedItemsForTargets(targets: Target[]): BlockedItems {
  return {
    appKeys: new Set(dedupeAppsFromTargets(targets).map((app) => app.key)),
    domains: normalizeDomains(dedupeDomainsFromTargets(targets)),
  };
}

export function deriveBlockedItemsForRule(
  rule: RuleConfig,
  targetsById: Map<string, Target>,
): BlockedItems {
  const targets = rule.targetIds
    .map((id) => targetsById.get(id))
    .filter((target): target is Target => Boolean(target));
  return deriveBlockedItemsForTargets(targets);
}

export function deriveBlockedItemsForRuleExcludingTarget(
  rule: RuleConfig,
  targetsById: Map<string, Target>,
  excludeTargetId: string,
): BlockedItems {
  const targets = rule.targetIds
    .filter((id) => id !== excludeTargetId)
    .map((id) => targetsById.get(id))
    .filter((target): target is Target => Boolean(target));
  return deriveBlockedItemsForTargets(targets);
}

function isBlockedItemsEmpty(items: BlockedItems): boolean {
  return items.appKeys.size === 0 && items.domains.size === 0;
}

function isBlockedItemsSubset(subset: BlockedItems, superset: BlockedItems): boolean {
  for (const key of subset.appKeys) {
    if (!superset.appKeys.has(key)) return false;
  }
  for (const domain of subset.domains) {
    if (!superset.domains.has(domain)) return false;
  }
  return true;
}

export function findCoveringEnforcingRuleForItems(
  subjectItems: BlockedItems,
  opts: {
    excludeRuleId?: string;
    excludeTargetIdFromRule?: string;
    allRules: Rule[];
    targetsById: Map<string, Target>;
    now: Date;
  },
): Rule | null {
  if (isBlockedItemsEmpty(subjectItems)) return null;

  for (const candidate of opts.allRules) {
    if (opts.excludeRuleId && candidate._id === opts.excludeRuleId) continue;
    if (!isRuleActivelyEnforcingForSwipeGuard(candidate, opts.now)) continue;

    const candidateItems =
      opts.excludeTargetIdFromRule && candidate.targetIds.includes(opts.excludeTargetIdFromRule)
        ? deriveBlockedItemsForRuleExcludingTarget(
            candidate,
            opts.targetsById,
            opts.excludeTargetIdFromRule,
          )
        : deriveBlockedItemsForRule(candidate, opts.targetsById);

    if (isBlockedItemsSubset(subjectItems, candidateItems)) {
      return candidate;
    }
  }

  return null;
}

export function isRuleFullyCoveredByAnotherEnforcingRule(
  rule: Rule,
  allRules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): boolean {
  if (!isRuleActivelyEnforcingForSwipeGuard(rule, now)) return false;

  const targetsById = new Map(targets.map((target) => [target._id, target]));
  const subjectItems = deriveBlockedItemsForRule(rule, targetsById);

  return (
    findCoveringEnforcingRuleForItems(subjectItems, {
      excludeRuleId: rule._id,
      allRules,
      targetsById,
      now,
    }) !== null
  );
}

export function isTargetFullyCoveredByAnotherEnforcingRule(
  target: Target,
  allRules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): boolean {
  const referencedByEnforcing = allRules.some(
    (rule) =>
      rule.targetIds.includes(target._id) && isRuleActivelyEnforcingForSwipeGuard(rule, now),
  );
  if (!referencedByEnforcing) return false;

  const targetsById = new Map(targets.map((entry) => [entry._id, entry]));
  const subjectItems = deriveBlockedItemsForTarget(target);

  return (
    findCoveringEnforcingRuleForItems(subjectItems, {
      excludeTargetIdFromRule: target._id,
      allRules,
      targetsById,
      now,
    }) !== null
  );
}
